
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trash2, Edit, Plus, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';

type License = Database['public']['Tables']['licenses']['Row'];

export function LicensesManagement() {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    max_calls_per_month: 0,
    max_campaigns: 0,
    is_active: true,
    features: {
      api_access: false,
      priority_support: false,
      advanced_analytics: false,
      white_label: false
    },
    credit_settings: {
      daily_bonus_credits: 0,
      rollover_cap: 100,
      rollover_expiration_days: 30,
      bonus_credits: 0,
      bonus_expiration_days: 0
    }
  });

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('licenses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLicenses(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch licenses",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const licenseData = {
        name: formData.name,
        description: formData.description,
        max_calls_per_month: formData.max_calls_per_month,
        max_campaigns: formData.max_campaigns,
        is_active: formData.is_active,
        features: {
          ...formData.features,
          credit_settings: formData.credit_settings
        }
      };

      if (editingLicense) {
        const { error } = await supabase
          .from('licenses')
          .update(licenseData)
          .eq('id', editingLicense.id);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "License updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('licenses')
          .insert(licenseData);

        if (error) throw error;
        
        toast({
          title: "Success", 
          description: "License created successfully",
        });
      }

      setIsDialogOpen(false);
      resetForm();
      fetchLicenses();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this license?')) return;
    
    try {
      const { error } = await supabase
        .from('licenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "License deleted successfully",
      });
      
      fetchLicenses();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (license: License) => {
    setEditingLicense(license);
    setFormData({
      name: license.name,
      description: license.description || '',
      max_calls_per_month: license.max_calls_per_month || 0,
      max_campaigns: license.max_campaigns || 0,
      is_active: license.is_active ?? true,
      features: {
        api_access: (license.features as any)?.api_access || false,
        priority_support: (license.features as any)?.priority_support || false,
        advanced_analytics: (license.features as any)?.advanced_analytics || false,
        white_label: (license.features as any)?.white_label || false
      },
      credit_settings: {
        daily_bonus_credits: (license.features as any)?.credit_settings?.daily_bonus_credits || 0,
        rollover_cap: (license.features as any)?.credit_settings?.rollover_cap || 100,
        rollover_expiration_days: (license.features as any)?.credit_settings?.rollover_expiration_days || 30,
        bonus_credits: (license.features as any)?.credit_settings?.bonus_credits || 0,
        bonus_expiration_days: (license.features as any)?.credit_settings?.bonus_expiration_days || 0
      }
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLicense(null);
    setFormData({
      name: '',
      description: '',
      max_calls_per_month: 0,
      max_campaigns: 0,
      is_active: true,
      features: {
        api_access: false,
        priority_support: false,
        advanced_analytics: false,
        white_label: false
      },
      credit_settings: {
        daily_bonus_credits: 0,
        rollover_cap: 100,
        rollover_expiration_days: 30,
        bonus_credits: 0,
        bonus_expiration_days: 0
      }
    });
  };

  const getCreditSummary = (license: License) => {
    const monthlyBase = license.max_calls_per_month || 0;
    const dailyBonus = (license.features as any)?.credit_settings?.daily_bonus_credits || 0;
    const rolloverCap = (license.features as any)?.credit_settings?.rollover_cap || 100;
    const bonusCredits = (license.features as any)?.credit_settings?.bonus_credits || 0;
    const bonusExpiry = (license.features as any)?.credit_settings?.bonus_expiration_days || 0;
    
    let summary = `${monthlyBase} monthly + ${dailyBonus} daily bonus + up to ${rolloverCap} rollover`;
    
    if (bonusCredits > 0) {
      const expiryText = bonusExpiry === 0 ? 'never expire' : `expire in ${bonusExpiry} days`;
      summary += ` + ${bonusCredits} license bonus (${expiryText})`;
    }
    
    return summary;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>License Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-muted rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>License Management</CardTitle>
        <CardDescription>
          Manage license tiers and their features. Monthly base credits set the recurring allocation, not a usage cap.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Add License
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingLicense ? 'Edit License' : 'Create License'}
                </DialogTitle>
                <DialogDescription>
                  Configure license features and credit allocations
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">License Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Pro, Enterprise"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="License description"
                    />
                  </div>
                </div>

                {/* Credit Configuration */}
                <div className="space-y-4">
                  <h4 className="font-medium">Credit Configuration</h4>
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Credits are consumed in order: Daily → Monthly → Rollover → Bonus → Purchased. Users can exceed monthly limits using rollover, bonus, and purchased credits.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="max_calls_per_month">Monthly Base Credits</Label>
                      <Input
                        id="max_calls_per_month"
                        type="number"
                        value={formData.max_calls_per_month}
                        onChange={(e) => setFormData({...formData, max_calls_per_month: parseInt(e.target.value) || 0})}
                        placeholder="100"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Monthly recurring credit allocation (not a usage cap)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="daily_bonus_credits">Daily Bonus Credits</Label>
                      <Input
                        id="daily_bonus_credits"
                        type="number"
                        value={formData.credit_settings.daily_bonus_credits}
                        onChange={(e) => setFormData({
                          ...formData, 
                          credit_settings: {
                            ...formData.credit_settings,
                            daily_bonus_credits: parseInt(e.target.value) || 0
                          }
                        })}
                        placeholder="5"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Daily bonus credits (expire daily if unused)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="rollover_cap">Rollover Cap</Label>
                      <Input
                        id="rollover_cap"
                        type="number"
                        value={formData.credit_settings.rollover_cap}
                        onChange={(e) => setFormData({
                          ...formData,
                          credit_settings: {
                            ...formData.credit_settings,
                            rollover_cap: parseInt(e.target.value) || 0
                          }
                        })}
                        placeholder="100"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximum credits that can rollover from previous month
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="rollover_expiration_days">Rollover Expiration (days)</Label>
                      <Input
                        id="rollover_expiration_days"
                        type="number"
                        value={formData.credit_settings.rollover_expiration_days}
                        onChange={(e) => setFormData({
                          ...formData,
                          credit_settings: {
                            ...formData.credit_settings,
                            rollover_expiration_days: parseInt(e.target.value) || 30
                          }
                        })}
                        placeholder="30"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Days before unused rollover credits expire
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="bonus_credits">License Bonus Credits</Label>
                      <Input
                        id="bonus_credits"
                        type="number"
                        value={formData.credit_settings.bonus_credits}
                        onChange={(e) => setFormData({
                          ...formData,
                          credit_settings: {
                            ...formData.credit_settings,
                            bonus_credits: parseInt(e.target.value) || 0
                          }
                        })}
                        placeholder="10"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        One-time bonus credits granted when license is assigned
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="bonus_expiration_days">Bonus Expiration (days)</Label>
                      <Input
                        id="bonus_expiration_days"
                        type="number"
                        value={formData.credit_settings.bonus_expiration_days}
                        onChange={(e) => setFormData({
                          ...formData,
                          credit_settings: {
                            ...formData.credit_settings,
                            bonus_expiration_days: parseInt(e.target.value) || 0
                          }
                        })}
                        placeholder="7"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Days before bonus credits expire (0 = never expire)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Campaign Limits */}
                <div>
                  <Label htmlFor="max_campaigns">Max Campaigns</Label>
                  <Input
                    id="max_campaigns"
                    type="number"
                    value={formData.max_campaigns}
                    onChange={(e) => setFormData({...formData, max_campaigns: parseInt(e.target.value) || 0})}
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Maximum number of active campaigns (0 = unlimited)
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <h4 className="font-medium">Features</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="api_access"
                        checked={formData.features.api_access}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          features: {...formData.features, api_access: checked}
                        })}
                      />
                      <Label htmlFor="api_access">API Settings Access</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="priority_support"
                        checked={formData.features.priority_support}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          features: {...formData.features, priority_support: checked}
                        })}
                      />
                      <Label htmlFor="priority_support">Priority Support</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="advanced_analytics"
                        checked={formData.features.advanced_analytics}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          features: {...formData.features, advanced_analytics: checked}
                        })}
                      />
                      <Label htmlFor="advanced_analytics">Advanced Analytics</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="white_label"
                        checked={formData.features.white_label}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          features: {...formData.features, white_label: checked}
                        })}
                      />
                      <Label htmlFor="white_label">White Label</Label>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    API Settings Access: Controls whether users can view/configure their API keys in the API Settings page
                  </p>
                </div>

                {/* Status */}
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingLicense ? 'Update' : 'Create'} License
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {licenses.map((license) => (
            <div key={license.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{license.name}</h3>
                  <Badge variant={license.is_active ? "default" : "secondary"}>
                    {license.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(license)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(license.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {license.description && (
                <p className="text-sm text-muted-foreground mb-3">{license.description}</p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-medium mb-1">Credit Allocation</div>
                  <div className="text-muted-foreground">
                    {getCreditSummary(license)}
                  </div>
                </div>
                
                <div>
                  <div className="font-medium mb-1">Campaign Limit</div>
                  <div className="text-muted-foreground">
                    {license.max_campaigns || 'Unlimited'} campaigns
                  </div>
                </div>
              </div>
              
              {license.features && Object.keys(license.features).length > 0 && (
                <div className="mt-3">
                  <div className="font-medium mb-2">Features</div>
                  <div className="flex flex-wrap gap-1">
                    {(license.features as any)?.api_access && (
                      <Badge variant="outline" className="text-xs">API Access</Badge>
                    )}
                    {(license.features as any)?.priority_support && (
                      <Badge variant="outline" className="text-xs">Priority Support</Badge>
                    )}
                    {(license.features as any)?.advanced_analytics && (
                      <Badge variant="outline" className="text-xs">Advanced Analytics</Badge>
                    )}
                    {(license.features as any)?.white_label && (
                      <Badge variant="outline" className="text-xs">White Label</Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {licenses.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No licenses found. Create your first license to get started.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
